import React, { forwardRef, memo } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Box } from '@mui/material'
import PageDivider from '@v4/components/common/PageDivider'
import PageTemplateData from '@v4/utils/data/PageTemplateData'
import AppThemeProvider from '@v4/components/common/AppThemeProvider'
import ThemeButton from './components/ThemeButton'
import SectionAction from './components/SectionAction'
import PDFContainer from './components/PDFContainer'
import Container from './components/Container'
import {
	SectionIdProvider,
	SectionKeyProvider,
	SectionMetaDataProvider,
	SectionActionsProvider,
	SectionIndexProvider,
} from './SectionItem.context'
import { useSectionItem } from './SectionItem.hooks'
import AddSection from './components/AddSection'

const SectionItem = forwardRef((props, ref) => {
	const [
		{
			Content,
			ContentElement,
			active,
			className,
			classes,
			collection,
			index,
			additionIndex,
			isPdf,
			metaData,
			parentId,
			sectionData,
			theme,
			withDivider,
			template,
			relatedSections,
			showSectionActions,
			id,
			showAddSectionBtn,
			hoverOffset,
			isSectionsLocked,
			sectionActions,
			withThemeButton,
			themeButtonVisible,
			isQuestionBuilderMode,
		},
		{ handleClick, handleAdd },
	] = useSectionItem(props)

	return Content ? (
		<AppThemeProvider theme={theme}>
			<SectionIdProvider value={sectionData.currentId}>
				<SectionKeyProvider value={sectionData.key}>
					<SectionIndexProvider value={additionIndex}>
						<SectionActionsProvider value={sectionActions}>
							<SectionMetaDataProvider value={metaData}>
								{withDivider && (
									<PageDivider disabled={isSectionsLocked} onClick={handleAdd}>
										Page {1 + index}
									</PageDivider>
								)}
								{showAddSectionBtn && (
									<AddSection
										offset={hoverOffset.top}
										onMouseEnter={sectionActions.handleMouseEnter}
										onMouseLeave={sectionActions.handleMouseLeave}
										index={additionIndex}
									/>
								)}
								<Box
									className={clsx(
										className,
										classes.root,
										classes[sectionData.parsedKey.group],
										sectionData.parsedKey.group,
										isPdf && PageTemplateData.isSortGroupMain(collection) && classes.pdf,
										'section',
										themeButtonVisible && classes.themeButtonVisible,
									)}
									id={`${parentId ? `parent-${parentId}-` : ''}section-${sectionData.currentId}`}
									color="text.primary"
									bgcolor="background.default"
									ref={ref}
									onClick={handleClick}
									onMouseEnter={sectionActions.handleMouseEnter}
									onMouseLeave={sectionActions.handleMouseLeave}
								>
									{(showSectionActions || active) && (
										<SectionAction
											title={template.title}
											withCopy={
												!template?.required &&
												PageTemplateData.isSortGroupMain(collection) &&
												!isSectionsLocked
											}
											withRemove={!template?.required && !isSectionsLocked}
											withToggle={!isSectionsLocked && template.visibility_toggle}
											sectionId={id}
											visibility={sectionData.visibility}
											offset={hoverOffset}
											dynamicGroup={_.get(template, 'fieldgroups.groups.logic.tier_group')}
											showContent={showSectionActions}
											sectionKey={sectionData.key}
										/>
									)}

									{isPdf ? (
										<PDFContainer active={active} collection={collection}>
											<Content
												variant={template.theme?.variant}
												index={index}
												type={sectionData.parsedKey.group}
												Element={ContentElement}
											/>
										</PDFContainer>
									) : (
										<Container
											className={clsx(classes.container, 'section-container')}
											collection={collection}
											relatedSections={relatedSections}
										>
											{withThemeButton && <ThemeButton className={classes.themeButton} />}
											<div className={clsx(classes.content, 'section-content')}>
												<Content
													key={isQuestionBuilderMode ? 'question' : 'page'}
													variant={template.theme?.variant}
													Element={ContentElement}
												/>
											</div>
										</Container>
									)}
								</Box>
								{showAddSectionBtn && (
									<AddSection
										onMouseEnter={sectionActions.handleMouseEnter}
										onMouseLeave={sectionActions.handleMouseLeave}
										index={additionIndex + 1}
										offset={-(hoverOffset.bottom || 0)}
									/>
								)}
							</SectionMetaDataProvider>
						</SectionActionsProvider>
					</SectionIndexProvider>
				</SectionKeyProvider>
			</SectionIdProvider>
		</AppThemeProvider>
	) : null
})

SectionItem.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	collection: PropTypes.string.isRequired,
	active: PropTypes.bool,
	className: PropTypes.string,
	index: PropTypes.number,
	additionIndex: PropTypes.number,
	parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	sectionsMap: PropTypes.object.isRequired,
	first: PropTypes.bool,
	last: PropTypes.bool,
}

export default memo(SectionItem)
